import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import './Select.css';

/**
 * Select Component
 * 
 * An accessible select/dropdown component with validation states.
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Selected value (controlled)
 * @param {string} props.defaultValue - Default value (uncontrolled)
 * @param {function} props.onChange - Change handler
 * @param {Array} props.options - Array of options [{value, label, disabled}]
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.required - Required field
 * @param {string} props.size - Select size: 'sm' | 'md' | 'lg'
 * @param {string} props.variant - Visual state: 'default' | 'error' | 'success' | 'warning'
 * @param {React.ReactNode} props.iconLeft - Icon on left side
 * @param {boolean} props.fullWidth - Full width select
 * @param {string} props.name - Select name attribute
 * @param {string} props.id - Select id attribute
 * @param {string} props.className - Additional CSS classes
 */
const Select = forwardRef(({
  value,
  defaultValue,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  required = false,
  size = 'md',
  variant = 'default',
  iconLeft = null,
  fullWidth = false,
  name,
  id,
  className = '',
  children,
  ...props
}, ref) => {
  const containerClasses = [
    'select-container',
    `select-container--${size}`,
    `select-container--${variant}`,
    fullWidth ? 'select-container--full-width' : '',
    disabled ? 'select-container--disabled' : '',
    iconLeft ? 'select-container--has-left-icon' : '',
    className
  ].filter(Boolean).join(' ');

  const selectClasses = [
    'select',
    `select--${size}`,
    iconLeft ? 'select--with-left-icon' : ''
  ].filter(Boolean).join(' ');

  // Use children if provided, otherwise use options prop
  const renderOptions = () => {
    if (children) {
      return children;
    }

    return (
      <>
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option, index) => (
          <option
            key={option.value || index}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </>
    );
  };

  return (
    <div className={containerClasses}>
      {iconLeft && (
        <span className="select__icon select__icon--left" aria-hidden="true">
          {iconLeft}
        </span>
      )}
      
      <select
        ref={ref}
        value={value}
        defaultValue={defaultValue || (placeholder ? '' : undefined)}
        onChange={onChange}
        disabled={disabled}
        required={required}
        name={name}
        id={id}
        className={selectClasses}
        aria-invalid={variant === 'error' ? 'true' : undefined}
        aria-required={required ? 'true' : undefined}
        {...props}
      >
        {renderOptions()}
      </select>
      
      <span className="select__arrow" aria-hidden="true">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </div>
  );
});

Select.displayName = 'Select';

Select.propTypes = {
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['default', 'error', 'success', 'warning']),
  iconLeft: PropTypes.node,
  fullWidth: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Select;
